import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import axios from 'axios';
import Card from './components/ui/Card';
import ThemeToggle from './components/ui/ThemeToggle';
import { API_BASE_URL } from './lib/api';
import { landingPathForUser } from './lib/landingPages';
import { isValidOauthReturnTo } from './lib/oauthReturnTo';
import './Login.css';

function Login() {
  const { login, setUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [devLoading, setDevLoading] = useState(false);
  const [devError, setDevError] = useState(null);
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoError, setDemoError] = useState(null);

  // Issue #257: a logged-out visit to /oauth/consent redirects here with
  // ?returnTo=<path+query> so any of the three sign-in paths can resume the
  // OAuth consent flow instead of landing on the user's normal landing page.
  // Only the exact consent-page shape is honoured (same regex as the
  // backend); anything else falls back to the normal post-login landing.
  const rawReturnTo = searchParams.get('returnTo');
  const returnTo = isValidOauthReturnTo(rawReturnTo) ? rawReturnTo : null;

  const landingHref = (user) => returnTo || landingPathForUser(user);

  const handleDemoLogin = async () => {
    setDemoLoading(true);
    setDemoError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/demo-login`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok && data.user) {
        // Set user in auth context
        setUser(data.user);
        // Land on whichever page this profile chose in Settings, unless a
        // valid returnTo (OAuth consent flow) takes priority.
        window.location.href = landingHref(data.user);
      } else {
        setDemoError(data.message || 'Demo login failed. Please try again.');
      }
    } catch (error) {
      console.error('Demo login failed:', error);
      setDemoError('Demo login failed. Please try again later.');
    } finally {
      setDemoLoading(false);
    }
  };

  const handleDevLogin = async () => {
    setDevLoading(true);
    setDevError(null);

    try {
      const response = await axios.post(`${API_BASE_URL}/auth/dev-login`);

      if (response.data.user) {
        // Set user in auth context
        setUser(response.data.user);
        // Land on whichever page this profile chose in Settings, unless a
        // valid returnTo (OAuth consent flow) takes priority.
        window.location.href = landingHref(response.data.user);
      }
    } catch (error) {
      console.error('Dev login failed:', error);
      setDevError(
        error.response?.data?.message ||
        'Dev login failed. Make sure ENABLE_DEV_AUTH=true in backend/.env'
      );
    } finally {
      setDevLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="theme-toggle-wrapper">
        <ThemeToggle />
      </div>
      <Card className="login-card" padding="lg">
        <h1>Portfolio Tracker</h1>
        <p className="login-subtitle">Track your investments with real-time prices</p>

        <div className="login-features">
          <div className="feature">
            <span className="feature-icon">📊</span>
            <span>Real-time portfolio tracking</span>
          </div>
          <div className="feature">
            <span className="feature-icon">💱</span>
            <span>Multi-currency support</span>
          </div>
          <div className="feature">
            <span className="feature-icon">📈</span>
            <span>Gain/loss calculations</span>
          </div>
          <div className="feature">
            <span className="feature-icon">💼</span>
            <span>Stocks, ETFs, crypto & more</span>
          </div>
        </div>

        {/* Demo Account - Prominent CTA for all visitors */}
        <button
          onClick={handleDemoLogin}
          className="demo-login-btn"
          disabled={demoLoading}
        >
          <span className="demo-icon">🚀</span>
          {demoLoading ? 'Loading demo...' : 'Try Demo Account'}
        </button>
        <p className="demo-helper-text">
          Explore with sample data - no sign-up required
        </p>
        {demoError && (
          <div className="demo-error">
            {demoError}
          </div>
        )}

        <div className="login-divider">
          <span>or sign in to save your data</span>
        </div>

        <button onClick={() => login(returnTo)} className="google-login-btn">
          <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            <path fill="none" d="M0 0h48v48H0z"/>
          </svg>
          Sign in with Google
        </button>

        {/* Dev Login - Only in development mode */}
        {process.env.NODE_ENV === 'development' && (
          <div className="dev-login-section">
            <button
              onClick={handleDevLogin}
              className="dev-login-btn"
              disabled={devLoading}
            >
              {devLoading ? 'Logging in...' : '🔧 Dev Login (Test Account)'}
            </button>
            {devError && (
              <div className="dev-error">
                {devError}
              </div>
            )}
          </div>
        )}

        <p className="login-privacy">
          We only access your basic profile information and do not store your Google password.
        </p>
      </Card>
    </div>
  );
}

export default Login;